import { Box, IconButton } from "@chakra-ui/react"
import * as React from "react"
import {
  FaMicrophone,
  FaMicrophoneSlash,
  FaVideo,
  FaVideoSlash
} from "react-icons/fa"
import { IoMdHand } from "react-icons/io"
import { useRecoilValue } from "recoil"
import { localUserIdAtom, usersSelector } from "../../../recoil/atoms/users"
import { UserPanel } from "./user-panel"
import { brandingSelector } from "../../../recoil/atoms/room"
import { Icon } from "../../common/icons"
import { hasPermission } from "../../../util"
import { showNoVideoSubscribeScreenSelector } from "../../../recoil/atoms/rtc"
const UserPermission = window.MeetOnline?.UserPermission
interface UserProps {
  userId: string
  isLast: boolean
  isAudience: boolean
  isModerator: boolean
}

const _User = ({ userId, isLast, isAudience, isModerator }: UserProps) => {
  const roomStyles = useRecoilValue(brandingSelector)
  const meetingClient = React.useRef(window.MeetOnline.client()).current
  const isMediaDisabled = useRecoilValue(showNoVideoSubscribeScreenSelector)

  const user = useRecoilValue(usersSelector(userId))
  const { micEnabled, camEnabled, id, permissions } = user

  const requestMicToggle = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      meetingClient?.toggleMic(micEnabled, id)
    },
    [id, meetingClient, micEnabled]
  )

  const requestCamToggle = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      meetingClient?.toggleCam(camEnabled, id)
    },
    [camEnabled, id, meetingClient]
  )

  const iconStyle = React.useMemo(() => {
    return {
      textColor: roomStyles?.servicePanelElementPrimaryText ?? "gray.900"
    }
  }, [roomStyles?.servicePanelElementPrimaryText])

  const iconDisabledStyle = React.useMemo(() => {
    return {
      textColor: roomStyles?.servicePanelElementPrimaryText ?? "gray.500"
    }
  }, [roomStyles?.servicePanelElementPrimaryText])

  const _localUserId = useRecoilValue(localUserIdAtom)
  const isLocalUser = user.id === _localUserId

  let micDisplay = React.useMemo(() => {
    const _hasPermission = hasPermission(permissions, UserPermission.canUseMic)
    const isEnabled = isLocalUser? _hasPermission && !isMediaDisabled : false
    return (
      <Box
        ml="0.2em !important"
        onClick={isEnabled ? requestMicToggle : undefined}
        {...(micEnabled ? iconStyle : iconDisabledStyle)}
      >
        <Icon
          src={micEnabled ? FaMicrophone : FaMicrophoneSlash}
          fontSize="1em"
          cursor={isEnabled ? "pointer" : ""}
        />
      </Box>
    )
  }, [
    iconDisabledStyle,
    iconStyle,
    micEnabled,
    isLocalUser,
    requestMicToggle,
    permissions,
    isMediaDisabled
  ])


  let camDisplay = React.useMemo(() => {
    const _hasPermission = hasPermission(permissions, UserPermission.canUseCam)
    const isEnabled = isLocalUser? _hasPermission && !isMediaDisabled : false
    return (
      <Box
        ml="0.2em !important"
        onClick={isEnabled ? requestCamToggle : undefined}
        {...(camEnabled ? iconStyle : iconDisabledStyle)}
      >
        <Icon
          src={camEnabled ? FaVideo : FaVideoSlash}
          fontSize="1em"
          cursor={isEnabled ? "pointer" : ""}
        />
      </Box>
    )
  }, [
    iconDisabledStyle,
    iconStyle,
    camEnabled,
    isLocalUser,
    requestCamToggle,
    permissions,
    isMediaDisabled
  ])

  let handRaisedDisplay = React.useMemo(
    () =>
      user.handUp === true ? <IoMdHand size="1em" color="#FFD700" /> : null,
    [user.handUp]
  )

  if (user.isModerator) {
    micDisplay = user.micEnabled ? (
      <IconButton
        variant="ghost"
        aria-label="Mic Enabled"
        icon={<FaMicrophone size="1em" />}
        {...iconStyle}
      />
    ) : (
      <IconButton
        variant="ghost"
        aria-label="Mic Disabled"
        icon={<FaMicrophoneSlash size="1em" />}
        {...iconDisabledStyle}
      />
    )
    camDisplay = user.camEnabled ? (
      <IconButton
        variant="ghost"
        aria-label="Camera Enabled"
        icon={<FaVideo size="1em" />}
        {...iconStyle}
      />
    ) : (
      <IconButton
        variant="ghost"
        aria-label="Camera Disabled"
        icon={<FaVideoSlash size="1em" />}
        {...iconDisabledStyle}
      />
    )
  }
  return (
    <>
      <UserPanel
        isAudience={isAudience}
        isModerator={isModerator}
        camDisplay={camDisplay}
        micDisplay={micDisplay}
        handRaisedDisplay={handRaisedDisplay}
        userName={user.name}
        avatar={user.avatar}
        userId={user.id}
        color={user?.color}
        expandedMenu={false}
        isLast={isLast}
        expandedStyles={{}}
      />
    </>
  )
}

export const User = React.memo(_User)
